import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { newsArticles } from '../data/sbbData';
import { Calendar, User, Share2, ChevronRight } from 'lucide-react';
import PageHero from '../components/PageHero';

export default function NewsArticleDetail() {
  const { postSlug } = useParams();
  const { lang, t } = useLanguage();

  const article = newsArticles.find((a) => a.slug === postSlug) || newsArticles[0];
  const relatedArticles = newsArticles.filter((a) => a.slug !== article.slug);

  const shareTitle = encodeURIComponent(article.title);
  const shareUrl = encodeURIComponent(window.location.href);

  return (
    <div className="news-article-detail-page">
      {/* Page Hero Header */}
      <PageHero eyebrow={`${article.type} • ${article.category}`} title={article.title} image={article.image} className="page-hero--article">
        <span className="meta-badge"><Calendar size={14} /> {article.date}</span>
        <span className="meta-badge"><User size={14} /> {article.author}</span>
      </PageHero>

      {/* Breadcrumbs */}
      <div className="breadcrumb-wrapper">
        <div className="container">
          <ul className="breadcrumb-list">
            <li><Link to="/">{t('nav_home')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><Link to="/blog">{t('nav_news')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><span className="current">{article.title}</span></li>
          </ul>
        </div>
      </div>

      {/* Article Content */}
      <section className="section">
        <div className="container" style={{ maxWidth: '840px' }}>
          <div className="article-body-wrapper">
            <img src={article.image} alt={article.title} className="article-featured-img img-rounded-shadow" />

            <div 
              className="article-rich-text"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Social Share Box (Section 5.10) */}
            <div className="social-share-box">
              <span className="share-title"><Share2 size={16} /> {t('share')} Publikasi Ini:</span>
              <div className="share-buttons">
                <a 
                  href={`https://wa.me/?text=${shareTitle}%20${shareUrl}`}
                  target="_blank" 
                  rel="noreferrer"
                  className="share-btn wa"
                >
                  WhatsApp
                </a>
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                  target="_blank" 
                  rel="noreferrer"
                  className="share-btn linkedin"
                >
                  LinkedIn
                </a>
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                  target="_blank" 
                  rel="noreferrer"
                  className="share-btn fb"
                >
                  Facebook
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related News */}
      {relatedArticles.length > 0 && (
        <section className="section section-subtle">
          <div className="container">
            <div className="section-header">
              <span className="accent-bar"></span>
              <h2 className="section-title">Berita & Artikel Terkait</h2>
            </div>

            <div className="grid-2">
              {relatedArticles.map((art) => (
                <div key={art.slug} className="sbb-card">
                  <img src={art.image} alt={art.title} className="sbb-card-img" />
                  <div className="sbb-card-body">
                    <span className="sbb-card-tag">{art.type} • {art.date}</span>
                    <h3 className="sbb-card-title">{art.title}</h3>
                    <p className="sbb-card-desc">{art.excerpt}</p>
                    <Link to={`/blog/${art.slug}`} className="btn btn-secondary btn-sm">
                      <span>{t('read_more')}</span>
                      <ChevronRight size={14} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <style>{`
        .hero-author-meta {
          display: flex;
          gap: 16px;
          margin-top: 16px;
          color: #d0d0d0;
          font-size: 14px;
        }

        .meta-badge {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .article-featured-img {
          width: 100%;
          height: 420px;
          object-fit: cover;
          margin-bottom: 32px;
        }

        .article-rich-text {
          font-size: 16px;
          color: var(--color-text);
          line-height: 1.8;

        }

        .article-rich-text p {
          margin-bottom: 20px;
        }

        .social-share-box {
          margin-top: 48px;
          padding-top: 24px;
          border-top: 1px solid var(--color-border);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .share-title {
          font-size: 14px;
          font-weight: 700;
          color: var(--color-ink);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .share-buttons {
          display: flex;
          gap: 10px;
        }

        .share-btn {
          font-size: 12px;
          font-weight: 700;
          padding: 6px 14px;
          border-radius: 4px;
          color: #ffffff;
        }

        .share-btn.wa { background-color: #25d366; }
        .share-btn.linkedin { background-color: #0077b5; }
        .share-btn.fb { background-color: #1877f2; }
      `}</style>
    </div>
  );
}
